import {
  type ExchangeRateMap,
  type ExchangeUnit,
  ExchangeUnitSchemaMap,
  type ToExchangeValue,
} from '@mntn-dev/domain-types'

export const DEFAULT_EXCHANGE_RATES: ExchangeRateMap = {
  DB: 1, // units stored in the database are DB
  USD: 100,
  mntn_credits: 50000, // 1 MNTN credit = 500 USD
  meta: 100,
  tiktok: 100,
  linkedin: 100,
}

/**
 * Converts an amount from one ExchangeUnit to another using the provided exchange rates
 * @param amount The amount to convert
 * @param fromUnit The source unit
 * @param toUnit The target unit
 * @param exchangeRates Map of exchange rates (each unit to its DB value)
 * @returns The converted amount in the target unit
 */
export function convertExchangeUnits<
  F extends ExchangeUnit,
  T extends ExchangeUnit,
>(
  amount: number,
  fromUnit: F,
  toUnit: T,
  exchangeRates?: ExchangeRateMap
): ToExchangeValue<T>
export function convertExchangeUnits(
  amount: number,
  fromUnit: string,
  toUnit: string,
  exchangeRates?: ExchangeRateMap
): number
export function convertExchangeUnits(
  amount: number,
  fromUnit: string,
  toUnit: string,
  exchangeRates: ExchangeRateMap = DEFAULT_EXCHANGE_RATES
): number {
  if (fromUnit === toUnit) {
    return amount
  }

  const fromRate = exchangeRates[fromUnit]

  if (fromRate === undefined) {
    throw new Error(`Missing exchange rate for unit: ${fromUnit}`)
  }

  const toRate = exchangeRates[toUnit]

  if (toRate === undefined) {
    throw new Error(`Missing exchange rate for unit: ${toUnit}`)
  }

  const baseValue = amount * fromRate
  const converted = baseValue / toRate

  return ExchangeUnitSchemaMap.get(toUnit as ExchangeUnit).parse(converted)
}
